import {useCallback, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';

import usePushNotificationSettingsChange from '@/hooks/pushNotifications/usePushNotificationSettingsChange';
import useOnFocusChange from '@/hooks/useOnFocusChange';
import amplitudeNotificationTracking from '@/services/analytics/amplitude/amplitudeNotificationTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import subscriptionService from '@/services/pushNotification/subscriptionService';
import {
    selectPushNotificationIsAnyOperationInProgress,
    selectPushNotificationIsSettingChangeInProgress,
    selectPushNotificationSubscription,
} from '@/state/selectors/pushNotification';

const useNotificationSettingsPage = () => {
    const currentSubscription = useSelector(selectPushNotificationSubscription);
    const isAnyOperationInProgress = useSelector(selectPushNotificationIsAnyOperationInProgress);
    const isSettingChangeInProgress = useSelector(selectPushNotificationIsSettingChangeInProgress);
    const {data: subscriptionData} = subscriptionService.getStoredSubscriptionInfo() || {};

    const valueFromApi = subscriptionData?.endpoint || subscriptionData?.token;
    const valueFromState = currentSubscription?.endpoint || currentSubscription?.token;
    const isNotificationEnabledFromState = !!(valueFromApi && valueFromApi === valueFromState);

    const {handleNotificationSettingChange} = usePushNotificationSettingsChange();

    useEffect(() => {
        googleAnalyticsService.trackPushNotificationsSettingScreenViewed(isNotificationEnabledFromState);
        amplitudeNotificationTracking.trackPushNotificationsSettingScreenViewed(isNotificationEnabledFromState);
    }, []);

    const form = useForm({
        defaultValues: {isNotificationEnabled: isNotificationEnabledFromState},
    });

    const updateFormValue = useCallback(
        (forceUpdate = false) => {
            if (isSettingChangeInProgress && !forceUpdate) return;

            form.setValue('isNotificationEnabled', isNotificationEnabledFromState);
        },
        [form, isNotificationEnabledFromState, isSettingChangeInProgress]
    );

    useEffect(() => {
        updateFormValue();
    }, [updateFormValue]);

    useOnFocusChange(updateFormValue);

    const handleSaveClick = useCallback(async () => {
        const {isNotificationEnabled} = form.getValues();

        // No changes, do nothing
        if (isNotificationEnabledFromState === isNotificationEnabled) return;

        await handleNotificationSettingChange(isNotificationEnabled, updateFormValue);
    }, [isNotificationEnabledFromState, handleNotificationSettingChange, updateFormValue]);

    return {
        form,
        handleSaveClick,
        isAnyOperationInProgress,
    };
};

export default useNotificationSettingsPage;
